import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ApiKeyResponseDto,
  CreateAdminApiKeyDto,
  UpdateAdminApiKeyDto,
} from '../common/model/dto/api-key';
import ApiKeyService from '../services/api-key.service';

export function useAdminApiKeyMutations() {
  const qc = useQueryClient();

  const invalidateList = () => qc.invalidateQueries({ queryKey: ['api-keys'] });

  const onApiKeySaved = (apiKey: ApiKeyResponseDto) => {
    qc.invalidateQueries({ queryKey: ['api-key', apiKey.id] });
    invalidateList();
  };

  const createApiKey = useMutation({
    mutationFn: (apiKey: CreateAdminApiKeyDto) =>
      ApiKeyService.createApiKey(apiKey),
    onSuccess: invalidateList,
  });

  const updateApiKey = useMutation({
    mutationFn: ({
      id,
      apiKey,
    }: {
      id: string;
      apiKey: UpdateAdminApiKeyDto;
    }) => ApiKeyService.updateApiKey(id, apiKey),
    onSuccess: onApiKeySaved,
  });

  const revokeApiKey = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      ApiKeyService.revokeApiKey(id, reason),
    onSuccess: onApiKeySaved,
  });

  const activateApiKey = useMutation({
    mutationFn: (id: string) => ApiKeyService.activateApiKey(id),
    onSuccess: onApiKeySaved,
  });

  const deleteApiKey = useMutation({
    mutationFn: (id: string) => ApiKeyService.deleteApiKey(id),
    onSuccess: (_, id) => {
      qc.removeQueries({ queryKey: ['api-key', id] });
      invalidateList();
    },
  });

  return {
    createApiKey,
    updateApiKey,
    revokeApiKey,
    activateApiKey,
    deleteApiKey,
  };
}
